import { CommonModule } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay, withI18nSupport } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './core/libs/shared/shared.module';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { GeneralModule } from './features/general/general.module';
import { MaterialModule } from './core/libs/material.module';
import { ENVIRONMENT } from '../environment/environment';
import { ACCESS_TOKEN_INTERCEPTOR } from './core/helpers/http-interceptors/access-token.interceptor';
import { HttpResponseInterceptor } from './core/helpers/http-interceptors/http-response.interceptor';
import { provideNgxStripe } from 'ngx-stripe';
import { provideToastr } from 'ngx-toastr';
import { AcceptLanguageInterceptor } from './core/helpers/http-interceptors/accept-language.interceptor';
import { SuccessToastrComponent } from './core/components/custom-toastr/success/success-toastr.component';
import { ErrorToastrComponent } from './core/components/custom-toastr/error/error-toastr.component';
import { InfoToastrComponent } from './core/components/custom-toastr/info/info-toastr.component';
import { WarningToastrComponent } from './core/components/custom-toastr/warning/warning-toastr.component';

import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';

/**
 * Main application module for the TaxPod customer frontend application.
 * 
 * **SSR Execution Context:**
 * - Runs on both server and client during Angular Universal SSR
 * - Server-side: Initializes core services, sets up providers, and prepares for hydration
 * - Client-side: Bootstraps the application and enables hydration with event replay
 * 
 * **Key SSR Features:**
 * - Client hydration with event replay and i18n support
 * - Firebase providers conditionally loaded only in browser environment
 * - HTTP interceptors for API communication and language headers
 * - Translation module with fallback language support
 * - Custom toastr components for user notifications
 * 
 * **Dependencies:**
 * - Angular Material for UI components
 * - ngx-translate for internationalization
 * - Firebase for remote configuration
 * - Stripe for payment processing
 * - reCAPTCHA for security
 * - Custom interceptors for API communication
 * 
 * @module AppModule
 * @description Root module that bootstraps the Angular application with SSR support
 */
@NgModule({
    declarations: [
        AppComponent,
        SuccessToastrComponent,
        ErrorToastrComponent,
        InfoToastrComponent,
        WarningToastrComponent
    ],
    imports: [
        AppRoutingModule,
        BrowserModule,
        CommonModule,
        GeneralModule,
        MaterialModule,
        RecaptchaV3Module,
        RouterModule,
        SharedModule,
        TranslateModule.forRoot({
            fallbackLang: 'en-US',
            loader: provideTranslateHttpLoader({prefix: './assets/i18n/', suffix: '.json'}),
        }),
        TranslateModule.forChild({
            extend: true,
        }),
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: HttpResponseInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AcceptLanguageInterceptor, multi: true },
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: ENVIRONMENT.recaptchaSiteKey },
        provideAnimationsAsync(),

        // Enable Angular hydration
        provideClientHydration(withEventReplay(), withI18nSupport()),

        provideHttpClient(withFetch(), withInterceptors([ACCESS_TOKEN_INTERCEPTOR]), withInterceptorsFromDi()),
        provideNgxStripe(),
        ...getFirebaseProviders(),
        provideToastr(),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

/**
 * Provides Firebase configuration providers with SSR compatibility.
 * 
 * **SSR Execution Behavior:**
 * - Server-side: Returns empty array to prevent Firebase initialization during SSR
 * - Client-side: Returns Firebase providers array with app initialization and remote config
 * 
 * **Why SSR-Safe:**
 * - Uses `typeof window !== 'undefined'` check to detect browser environment
 * - Prevents Firebase SDK initialization on server where `window` object doesn't exist
 * - Avoids server-side errors related to browser-only Firebase features
 * 
 * **Firebase Services Provided:**
 * - Firebase App initialization with environment configuration
 * - Remote Config service for feature flags and dynamic configuration
 * 
 * @function getFirebaseProviders
 * @returns {Provider[]} Array of Firebase providers for browser environment, empty array for server
 * @description Conditionally provides Firebase services only in browser environment for SSR compatibility
 */
export function getFirebaseProviders() {
    // Only initialize Firebase in browser environment
    if (typeof window !== 'undefined') {
        return [
            provideFirebaseApp(() => initializeApp(ENVIRONMENT.firebaseConfig)),
            provideRemoteConfig(() => getRemoteConfig()),
        ];
    }
    return [];
}
